// Compose a Social Connect post from the web console.
//
// ── WHY THIS CAN 403 WHEN THE PAGE AROUND IT WORKS ──────────────────────────
//
// The moderation tabs are authorised by `web.social_moderate` + project-wide data
// scope. THIS talks to the employee-facing feed routes, which require the MOBILE keys
// (`social_feed` + `social_post_create`) on a role the caller holds in this project,
// and have no super-admin bypass. So a platform admin can be looking at every post in
// the project and still be refused permission to write one. That is the server being
// correct — an admin posts AS THEMSELVES, so they need the same standing as anyone
// else. The composer surfaces it as an explanation, never a retry.
//
// ── THE AUDIENCE LIST IS THE SERVER'S, NOT OURS ─────────────────────────────
//
// Options come from GET /social-feed/audience-options, including the targeted ones
// (a management level, one manager's whole team). Nothing here decides who can be
// reached: createPost re-derives and re-authorises every field, so a stale list can
// only ever show an option the submit then refuses. Rendering the server's answer is
// also what lets a new target kind appear with no front-end release.
//
// Announcement audience is FORCED server-side, which is why picking Announcement
// locks the selector instead of offering choices that would be discarded.
//
// ── THE CONNECT CONTENT POLICY ANSWERS HERE TOO ─────────────────────────────
//
// Console posts go through the same AI review as mobile ones. Three answers, none of
// them a 403 (which would wrongly open the "no posting access" panel below):
//   • 422 CONNECT_POLICY_BLOCKED — not posted. Shown as its own panel naming each rule
//     and its fix, with the offending photos ringed. Text and photos stay as they are,
//     so the fix is an edit, not a rewrite.
//   • 429 CONNECT_POLICY_LIMIT — too many blocked attempts today; the server's message.
//   • 2xx with review.state 'held' — saved, but only the author sees it until a
//     moderator approves it. Said out loud before the modal closes, or the moderator
//     would go looking for a post that "didn't appear".

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Image as ImageIcon, Loader2, AlertCircle, Megaphone, Users, ShieldAlert, Clock } from 'lucide-react';
import { InlineLoader } from '../loading';
import { socialFeedAPI } from '../../services/api';

/** Server limits, mirrored so the composer refuses before a round trip. The server
 *  re-checks all of them; these exist to explain, not to enforce. */
const MAX_CHARS = 4000;
const MAX_PHOTOS = 4;

interface AudienceOption { value: string; label: string; hint?: string }

interface UploadedPhoto {
  key: string;
  name: string;
  blobPath: string;
  /** Local object URL for the thumbnail — revoked on removal and unmount. */
  previewUrl: string;
}

/** A rule the AI cited, as the write path returns it. `mediaIndex` is 0-based into the
 *  mediaItems SENT — which is `photos` in order, since every photo here is uploaded
 *  before it is listed. */
interface PolicyRuleHit { id: string; name: string; fix: string; mediaIndex: number | null }

interface PolicyBlock {
  message: string | null;
  rules: PolicyRuleHit[];
  /** Photo KEYS, not indexes, resolved at the moment of the block: removing photo 1
   *  afterwards must not move the red ring onto the photo that was fine. */
  flaggedKeys: Set<string>;
}

interface Props {
  projectId: number;
  onClose: () => void;
  /** Fired after a successful publish so the page can refetch. */
  onPublished: () => void;
}

export default function PostComposerModal({ projectId, onClose, onPublished }: Props) {
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [options, setOptions] = useState<AudienceOption[]>([]);
  const [targetOptions, setTargetOptions] = useState<AudienceOption[]>([]);
  const [canAnnounce, setCanAnnounce] = useState(false);
  const [announcementLabel, setAnnouncementLabel] = useState<string | null>(null);

  const [isAnnouncement, setIsAnnouncement] = useState(false);
  // One control for both lists: a plain audience ('project'/'team'/'peers') or a
  // target ('level:th', 'mgr:…'). Keeping them in one value is what makes the
  // selector a single question — "who is this for" — rather than two that can
  // contradict each other.
  const [selected, setSelected] = useState('project');
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [blocked, setBlocked] = useState<PolicyBlock | null>(null);
  /** Set once a post was saved but HELD for review — the composer then shows only
   *  the notice and a Done button. */
  const [heldNotice, setHeldNotice] = useState<string | null>(null);

  // ── @-TAGGING ─────────────────────────────────────────────────────────────
  //
  // The trigger deliberately stops at whitespace: `@` followed by a run of non-space
  // characters immediately before the caret. Names contain spaces, so allowing them in
  // the QUERY would mean the picker never closes — every space would extend the search
  // instead of ending it. People type "@rak" and pick; they don't type the full name.
  const MENTION_TRIGGER = /@([^\s@]{1,30})$/;

  const [tagged, setTagged] = useState<Array<{ empCode: string; name: string }>>([]);
  const [tagQuery, setTagQuery] = useState<string | null>(null);
  const [tagResults, setTagResults] = useState<Array<{ empCode: string; name: string; designation: string | null }>>([]);
  const [tagLoading, setTagLoading] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  // Re-read the trigger on every change AND on caret moves — clicking into the middle
  // of an existing "@Rakesh" should not reopen the picker for a name already chosen,
  // which is why this keys off the text BEFORE the caret rather than the whole value.
  const syncTrigger = useCallback((value: string, caret: number) => {
    const m = MENTION_TRIGGER.exec(value.slice(0, caret));
    setTagQuery(m ? m[1] : null);
  }, []);

  useEffect(() => {
    if (tagQuery === null || tagQuery.length < 2) { setTagResults([]); return; }
    let alive = true;
    setTagLoading(true);
    // Debounced: this fires per keystroke and each one is a project-wide people search.
    const t = setTimeout(() => {
      socialFeedAPI
        .searchPeople(projectId, tagQuery)
        .then((res) => { if (alive) setTagResults(res.data?.data || []); })
        .catch(() => { if (alive) setTagResults([]); })
        .finally(() => { if (alive) setTagLoading(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [tagQuery, projectId]);

  /** Swap the partial "@rak" for "@Rakesh Menon " and remember the code. */
  const chooseTag = (p: { empCode: string; name: string }) => {
    const el = textRef.current;
    const caret = el?.selectionStart ?? text.length;
    const before = text.slice(0, caret);
    const m = MENTION_TRIGGER.exec(before);
    if (!m) return;
    const next = `${before.slice(0, m.index)}@${p.name} ${text.slice(caret)}`;
    setText(next);
    setTagged((prev) => (prev.some((x) => x.empCode === p.empCode) ? prev : [...prev, p]));
    setTagQuery(null);
    setTagResults([]);
    // Put the caret after the inserted name, or the next character typed lands inside it.
    const pos = m.index + p.name.length + 2;
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(pos, pos); });
  };

  // Only codes whose name SURVIVES in the final text are sent. Deleting "@Rakesh Menon"
  // by hand has to untag him — otherwise the post grants him access to a restricted
  // post that no longer names him, and he gets a "tagged you" push for it.
  const liveMentions = (body: string) =>
    tagged.filter((t) => body.includes(`@${t.name}`)).map((t) => t.empCode);

  const fileRef = useRef<HTMLInputElement>(null);
  const photosRef = useRef<UploadedPhoto[]>([]);
  photosRef.current = photos;

  // Revoke every object URL on unmount. A console session opens a lot of these and
  // each un-revoked URL pins a decoded bitmap for the life of the tab.
  useEffect(() => () => { photosRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl)); }, []);

  useEffect(() => {
    let alive = true;
    setLoadingOptions(true);
    socialFeedAPI
      .getAudienceOptions(projectId)
      .then((res) => {
        if (!alive) return;
        const d = res.data || {};
        setOptions(d.options || []);
        setTargetOptions(d.targetOptions || []);
        setCanAnnounce(!!d.canAnnounce);
        setAnnouncementLabel((d.announcementOptions || [])[0]?.label ?? null);
        setSelected((d.options || [])[0]?.value ?? 'project');
      })
      .catch((err) => {
        if (!alive) return;
        // 403 here means "not a poster in this project", which is a standing state
        // and not a failure — it gets its own panel rather than an error toast.
        if (err?.response?.status === 403) setDenied(true);
        else setError('Could not load audience options.');
      })
      .finally(() => { if (alive) setLoadingOptions(false); });
    return () => { alive = false; };
  }, [projectId]);

  const isTarget = selected.includes(':');
  const selectedHint = useMemo(() => {
    const all = [...options, ...targetOptions];
    return all.find((o) => o.value === selected)?.hint ?? null;
  }, [options, targetOptions, selected]);

  const addPhotos = useCallback(async (files: FileList | null) => {
    if (!files?.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files).slice(0, room)) {
        const res = await socialFeedAPI.uploadPhoto(file);
        // /upload/file answers { success, data: { fileName, blobPath, url } }.
        const blobPath: string | undefined = res.data?.data?.blobPath;
        if (!blobPath) throw new Error('Upload returned no path');
        setPhotos((prev) => [...prev, {
          key: `${Date.now()}-${file.name}`,
          name: file.name,
          blobPath,
          previewUrl: URL.createObjectURL(file),
        }]);
      }
    } catch {
      setError('That photo could not be uploaded. Please try a different file.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [photos.length]);

  const removePhoto = (key: string) => {
    setPhotos((prev) => {
      const hit = prev.find((p) => p.key === key);
      if (hit) URL.revokeObjectURL(hit.previewUrl);
      return prev.filter((p) => p.key !== key);
    });
  };

  const overLimit = text.length > MAX_CHARS;
  const canPublish = !publishing && !uploading && !overLimit && (text.trim().length > 0 || photos.length > 0);

  const publish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    setError(null);
    setBlocked(null);
    try {
      const res = await socialFeedAPI.createPost({
        projectId,
        content: text.trim(),
        postType: isAnnouncement ? 'announcement' : 'post',
        // Exactly one of these carries the answer. An announcement sends neither —
        // the server forces its audience and rejects a target on one outright.
        ...(isAnnouncement ? {} : isTarget ? { audienceTarget: selected } : { audience: selected }),
        mediaItems: photos.map((p) => ({ blobPath: p.blobPath })),
        // Only the tags whose name is still in the text — see liveMentions.
        mentions: liveMentions(text.trim()),
      });
      // The list behind shows held posts too (as "Held for review"), so refetch either way.
      onPublished();
      const review = res.data?.review;
      if (review?.state === 'held') {
        setHeldNotice(
          typeof review.message === 'string' && review.message.trim()
            ? review.message
            : 'Your post will be shared after a moderator reviews it. Only you can see it until then.',
        );
        return;
      }
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      // The server's own message is better than anything generic we could write: it
      // names the group that was too large, or says the group has nobody in it.
      const msg = body?.message;
      if (status === 422 && body?.code === 'CONNECT_POLICY_BLOCKED') {
        const rules: PolicyRuleHit[] = Array.isArray(body?.review?.rules) ? body.review.rules : [];
        const flaggedKeys = new Set<string>();
        rules.forEach((r) => {
          const hit = r.mediaIndex != null ? photos[r.mediaIndex] : undefined;
          if (hit) flaggedKeys.add(hit.key);
        });
        setBlocked({ message: typeof msg === 'string' ? msg : null, rules, flaggedKeys });
      } else if (status === 403) setDenied(true);
      // Both 429s — the daily posting cap and CONNECT_POLICY_LIMIT (too many blocked
      // attempts today) — carry a message that says which, so it is shown as sent.
      else if (status === 429) setError(msg || 'You have hit today’s posting limit. Try again later.');
      else setError(msg || 'Could not publish that post.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-900/50 p-4 backdrop-blur-sm sm:p-8"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isAnnouncement ? 'New announcement' : 'New post'}
          </h2>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {heldNotice ? (
          <>
            <div className="px-5 py-8">
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-900">
                <Clock size={18} className="mt-0.5 flex-none text-amber-600" />
                <div>
                  <div className="font-semibold">Sent for review — a moderator will check it before it’s shared</div>
                  <p className="mt-1 whitespace-pre-line text-amber-800">{heldNotice}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end rounded-b-xl border-t border-gray-100 bg-gray-50 px-5 py-3.5">
              <button onClick={onClose} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                Done
              </button>
            </div>
          </>
        ) : denied ? (
          <div className="px-5 py-10 text-center">
            <AlertCircle className="mx-auto mb-3 text-amber-500" size={28} />
            <p className="mx-auto max-w-md text-sm text-gray-600">
              You don’t have posting access in this project. Posting from here publishes
              <span className="font-medium text-gray-800"> under your own name</span>, so it needs
              either the <span className="font-medium text-gray-800">Social Moderation</span> web
              feature for this project, or the Connect access an employee has
              (<span className="font-medium text-gray-800">View Feed</span> and
              <span className="font-medium text-gray-800"> Create Posts &amp; Comments</span> under
              Mobile Access). Both are granted in Settings → Access Management.
            </p>
          </div>
        ) : loadingOptions ? (
          <InlineLoader label="Loading audience options…" className="flex justify-center px-5 py-14" />
        ) : (
          <>
            <div className="space-y-5 px-5 py-5">
              {canAnnounce && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Type</label>
                  <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
                    <button
                      onClick={() => setIsAnnouncement(false)}
                      className={`px-4 py-2 text-sm font-medium ${!isAnnouncement ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >Post</button>
                    <button
                      onClick={() => setIsAnnouncement(true)}
                      className={`border-l border-gray-200 px-4 py-2 text-sm font-medium ${isAnnouncement ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    ><Megaphone size={14} className="mr-1.5 inline" />Announcement</button>
                  </div>
                  {isAnnouncement && (
                    <p className="mt-1.5 text-xs text-gray-500">Pinned to the top of the feed. Separate daily limit.</p>
                  )}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" htmlFor="aud">
                  Audience
                </label>
                {isAnnouncement ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                    <Users size={14} className="mr-1.5 inline" />
                    {announcementLabel || 'Decided by your access level'}
                    <span className="mt-0.5 block text-xs text-amber-700">
                      An announcement’s audience is set by the server and can’t be changed here.
                    </span>
                  </div>
                ) : (
                  <>
                    <select
                      id="aud"
                      value={selected}
                      onChange={(e) => setSelected(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                    >
                      <optgroup label="Everyone and your own line">
                        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </optgroup>
                      {targetOptions.length > 0 && (
                        <optgroup label="A specific group">
                          {targetOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </optgroup>
                      )}
                    </select>
                    {selectedHint && <p className="mt-1.5 text-xs text-gray-500">{selectedHint}</p>}
                  </>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" htmlFor="body">
                  Message
                </label>
                <textarea
                  ref={textRef}
                  id="body"
                  value={text}
                  onChange={(e) => { setText(e.target.value); syncTrigger(e.target.value, e.target.selectionStart ?? 0); }}
                  // Caret moves without a text change also open or close the picker —
                  // clicking away from a half-typed "@rak" must dismiss it.
                  onKeyUp={(e) => syncTrigger(text, (e.target as HTMLTextAreaElement).selectionStart ?? 0)}
                  onClick={(e) => syncTrigger(text, (e.target as HTMLTextAreaElement).selectionStart ?? 0)}
                  onBlur={() => setTimeout(() => setTagQuery(null), 150)} // after the click lands
                  rows={5}
                  placeholder="Write something for the team… use @ to tag a colleague"
                  className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                />

                {/* Anchored under the textarea rather than at the caret. Caret-anchored
                    menus need a mirrored-div measurement that breaks on resize, wrap and
                    scroll; a fixed position under the field is unglamorous and always
                    lands in the right place. */}
                {tagQuery !== null && tagQuery.length >= 2 && (
                  <div className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {tagLoading && tagResults.length === 0 ? (
                      <InlineLoader label="Searching…" className="flex px-3 py-2.5" />
                    ) : tagResults.length === 0 ? (
                      <div className="px-3 py-2.5 text-sm text-gray-500">
                        No one in this project matches “{tagQuery}”.
                      </div>
                    ) : (
                      tagResults.map((p) => (
                        <button
                          key={p.empCode}
                          type="button"
                          // onMouseDown, not onClick: the textarea's blur fires first and
                          // would close the list before a click could register.
                          onMouseDown={(e) => { e.preventDefault(); chooseTag(p); }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                        >
                          <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-blue-50 text-[11px] font-semibold text-blue-700">
                            {p.name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?'}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm text-gray-900">{p.name}</span>
                            {p.designation && (
                              <span className="block truncate text-xs text-gray-500">{p.designation}</span>
                            )}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}

                <div className="mt-1 flex items-center justify-between gap-3">
                  <span className="text-xs text-gray-500">
                    {liveMentions(text).length > 0
                      ? `Tagging ${liveMentions(text).length} ${liveMentions(text).length === 1 ? 'person' : 'people'}`
                      : ''}
                  </span>
                  <span className={`text-xs tabular-nums ${overLimit ? 'font-semibold text-red-600' : 'text-gray-400'}`}>
                    {text.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
                  </span>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Photos</label>
                <div className="flex flex-wrap gap-2">
                  {photos.map((p, i) => (
                    <div key={p.key} className="relative">
                      <img
                        src={p.previewUrl}
                        alt={p.name}
                        className={`h-20 w-20 rounded-lg border object-cover ${
                          blocked?.flaggedKeys.has(p.key) ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-200'
                        }`}
                      />
                      {blocked?.flaggedKeys.has(p.key) && (
                        // 1-based, matching the numbering in the rule list above and in
                        // the server's message.
                        <span className="absolute bottom-1 left-1 rounded bg-red-600 px-1 text-[10px] font-semibold text-white">
                          Photo {i + 1}
                        </span>
                      )}
                      <button
                        onClick={() => removePhoto(p.key)}
                        className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:text-red-600"
                        aria-label={`Remove ${p.name}`}
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                  {photos.length < MAX_PHOTOS && (
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="grid h-20 w-20 place-items-center rounded-lg border border-dashed border-gray-300 text-gray-400 hover:border-blue-500 hover:text-blue-600 disabled:opacity-50"
                      aria-label="Add a photo"
                    >
                      {uploading ? <Loader2 className="animate-spin" size={18} /> : <ImageIcon size={18} />}
                    </button>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  hidden
                  onChange={(e) => addPhotos(e.target.files)}
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  {photos.length >= MAX_PHOTOS ? 'Maximum of 4 photos reached.' : 'Up to 4 photos. JPEG, PNG or WebP.'}
                </p>
              </div>

              {blocked && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                  <div className="flex items-center gap-2 font-semibold text-red-700">
                    <ShieldAlert size={16} className="flex-none" />
                    This {isAnnouncement ? 'announcement' : 'post'} can’t be shared
                  </div>
                  {blocked.rules.length > 0 ? (
                    <>
                      <p className="mt-1 text-red-700">It goes against the Connect content policy.</p>
                      <ul className="mt-2 space-y-1.5">
                        {blocked.rules.map((r, i) => (
                          <li key={`${r.id}:${i}`} className="flex gap-2">
                            <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-red-400" />
                            <span>
                              <span className="font-medium">{r.name}</span>
                              {r.mediaIndex != null && <span className="text-red-600"> (Photo {r.mediaIndex + 1})</span>}
                              {r.fix ? <>: {r.fix}</> : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    // An API that sends no rule list still sends a self-contained
                    // message (it is what old mobile builds show), so fall back to it.
                    <p className="mt-1 whitespace-pre-line text-red-700">
                      {blocked.message || 'It goes against the Connect content policy.'}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-red-600">Your text and photos are kept — edit and publish again.</p>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                  <AlertCircle size={15} className="mt-0.5 flex-none" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3.5">
              {/* In enforce mode the AI check runs before the post is saved and can take
                  several seconds; without this the spinner reads as a hang. */}
              {publishing && (
                <span className="mr-auto text-xs text-gray-500">Checking your post…</span>
              )}
              <button onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={publish}
                disabled={!canPublish}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {publishing && <Loader2 className="animate-spin" size={14} />}
                Publish
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
